import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { BankDetailsDisplay } from "@/components/BankDetailsFields";
import type { components } from "@dolfin/api-admin";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

type SupplierResponse = components["schemas"]["SupplierResponse"];

interface ConfirmBankDetailsDialogProps {
  supplier: SupplierResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmed: () => void;
}

/** Echoes the exact details shown so the backend can reject if they changed underneath. */
export function ConfirmBankDetailsDialog({
  supplier,
  open,
  onOpenChange,
  onConfirmed,
}: ConfirmBankDetailsDialogProps) {
  const api = useDolfinClient();
  const [confirming, setConfirming] = useState(false);

  async function handleConfirm() {
    if (confirming || !supplier.id) return;
    setConfirming(true);
    try {
      const { data, error } = await api.POST("/v1/suppliers/{id}/confirm-bank-details", {
        params: { path: { id: supplier.id } },
        body: {
          sortCode: supplier.bankDetails?.sortCode ?? null,
          accountNumber: supplier.bankDetails?.accountNumber ?? null,
          iban: supplier.bankDetails?.iban ?? null,
          routingNumber: supplier.bankDetails?.routingNumber ?? null,
        },
      });
      if (error || !data) {
        toast.error(apiErrorMessage(error, "Failed to confirm bank details"));
        return;
      }
      toast.success("Bank details confirmed for pay-by-bank");
      onOpenChange(false);
      onConfirmed();
    } catch {
      toast.error("Failed to confirm bank details");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5" />
            Confirm bank details
          </DialogTitle>
          <DialogDescription>
            Payments to {supplier.name} will be sent to this account. Check the details against a
            trusted source (e.g. a verified invoice or direct contact) before confirming - bank
            payments cannot be recalled.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-muted/40 p-3">
          <BankDetailsDisplay bankDetails={supplier.bankDetails} />
        </div>

        <DialogFooter>
          <Button variant="ghost" disabled={confirming} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={confirming} onClick={handleConfirm}>
            {confirming ? <Spinner className="size-4" /> : <ShieldCheck className="size-4" />}
            These details are correct
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
